// src/pages/IndoorMap/IndoorMap.jsx
import React, { useState } from "react"
import { ArrowLeftRight, ChevronDown, MapPin, X } from "lucide-react"
import { ROOMS } from "./mapData"
import { resolveRoute } from "./routeAssets"
import RouteOverlay from "./RouteOverlay"
import RouteInfoPanel from "./RouteInfoPanel"
import RoomMarkers from "./RoomMarkers"
import mapImage from "../../assets/images/InDoorMap-names1.png"
import "./IndoorMap.css"
import Logo from "../../assets/images/brands/converge.png"
import { useHistory } from "react-router-dom"

// Same decorative-background recipe as AgendaModal's BackgroundBlobs,
// re-anchored for a full-page (non-modal) container.
const IndoorMapBlobs = () => (
  <div className="indoor-map-blobs" aria-hidden="true">
    <div className="indoor-map-beam-primary" />
    <div className="indoor-map-beam-secondary" />
    <div className="indoor-map-blob-glow" />
  </div>
)

const IndoorMap = () => {
    const history = useHistory()
  const [startRoom, setStartRoom] = useState("")
  const [endRoom, setEndRoom] = useState("")

  const route = resolveRoute(startRoom, endRoom)
  const noRouteFound =
    !!startRoom && !!endRoom && startRoom !== endRoom && !route

  const handleSwap = () => {
    setStartRoom(endRoom)
    setEndRoom(startRoom)
  }

  const handleClear = () => {
    setStartRoom("")
    setEndRoom("")
  }

  // const handleHeaderBack = () => {
  //   if (isNYUC && (view === "event" || view === "networking")) {
  //     setView("select")
  //   } else {
  //     goBackToDashboard()
  //   }
  // }

  const goBackToLobby = () => history.push("/page/lobby")


  // Derives each map dot's visual/interaction state purely from the two
  // pieces of state above — no extra state to keep in sync.
  //   idle      -> nothing picked yet, every dot is up for grabs
  //   pickable  -> one role is filled, this dot can fill the other
  //   start/end -> this dot IS the currently filled role
  //   disabled  -> both roles filled by other dots; inert until one clears
  const getDotRole = roomId => {
    if (roomId === startRoom) return "start"
    if (roomId === endRoom) return "end"
    if (!startRoom && !endRoom) return "idle"
    if (startRoom && endRoom) return "disabled"
    return "pickable"
  }

  // Clicking a dot that's already a role clears that role. Clicking any
  // other (non-disabled) dot fills whichever role is currently empty —
  // so re-picking after a clear always lands back in the role that was
  // just vacated, never overwrites the role still in place.
  const handleDotClick = roomId => {
    if (roomId === startRoom) {
      setStartRoom("")
      return
    }
    if (roomId === endRoom) {
      setEndRoom("")
      return
    }
    if (startRoom && endRoom) return // both roles taken elsewhere; no-op
    if (!startRoom) {
      setStartRoom(roomId)
    } else {
      setEndRoom(roomId)
    }
  }

  return (
    <div className="indoor-map-wrap">
       
      {/* <IndoorMapBlobs /> */}

      <div className="indoor-map-content">
        <div className="feedback-page-header">
        <button className="fb-back-link" onClick={goBackToLobby}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28"
            fill="white" viewBox="0 0 24 24" >
            <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8"></path><path d="m12.71 8.71-1.42-1.42L6.59 12l4.7 4.71 1.42-1.42-2.3-2.29H17v-2h-6.59z"></path>
          </svg>
        </button>
        <span className="fb-page-title">Indoor Maps</span>
      </div>
        {/* <div className="indoor-map-header">
          <MapPin size={18} className="indoor-map-header-icon" />
          <span className="indoor-map-title">Indoor Map</span>
        </div> */}

        <div className="indoor-map-controls">
          <div className={`indoor-map-select-wrap indoor-map-select-start ${startRoom ? "is-selected" : ""}`}>
            <select
              value={startRoom}
              onChange={e => setStartRoom(e.target.value)}
            >
              <option value="">Start location...</option>
              {ROOMS.map(r => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="indoor-map-select-chevron" />
          </div>

          <button
            type="button"
            className="indoor-map-swap-btn"
            onClick={handleSwap}
            disabled={!startRoom && !endRoom}
            aria-label="Swap start and destination"
          >
            <ArrowLeftRight size={16} />
          </button>

          <div className={`indoor-map-select-wrap indoor-map-select-end ${endRoom ? "is-selected" : ""}`}>
            <select value={endRoom} onChange={e => setEndRoom(e.target.value)}>
              <option value="">Destination...</option>
              {ROOMS.map(r => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="indoor-map-select-chevron" />
          </div>

          <button
            type="button"
            className="indoor-map-swap-btn indoor-map-clear-btn"
            onClick={handleClear}
            disabled={!startRoom && !endRoom}
            aria-label="Clear selection"
          >
            <X size={16} />
          </button>
        </div>

        {noRouteFound && (
          <div className="indoor-map-warning">
            No route drawn yet between these two rooms.
          </div>
        )}

        <div className="indoor-map-stage">
          <img
            src={mapImage}
            alt="Venue floor map"
            className="indoor-map-image"
          />

          <RouteOverlay
            RouteComponent={route?.Component}
            active={!!route}
            routeKey={`${startRoom}|${endRoom}`}
            swapped={route?.swapped}
          />

          <RoomMarkers
            rooms={ROOMS}
            getDotRole={getDotRole}
            onDotClick={handleDotClick}
          />
        </div>
        {startRoom && <RouteInfoPanel startId={startRoom} endId={endRoom} />}

        <div className="feedback-page-footer">
        <span className="fb-page-title">Walmart</span>
        <img src={Logo}/>
      </div>
      </div>
    </div>
  )
}

export default IndoorMap